<template>
  <div class="icon-picker">
    <div class="selected-icon" :class="{ 'disabled': disabled }" @click="togglePicker">
      <div class="icon-preview">
        <i v-if="modelValue" :class="modelValue"></i>
        <span v-else class="placeholder">選擇圖示</span>
      </div>
      <span class="icon-name">{{ modelValue || '未選擇' }}</span>
      <i class="ri-arrow-down-s-line toggle-arrow" :class="{ 'open': showPicker }"></i>
    </div>

    <div v-if="showPicker" class="icon-picker-panel">
      <div class="picker-header">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="搜尋圖示..."
          @input="handleSearch"
        />
        <button type="button" class="btn-clear" @click="clearSelection">
          清除
        </button>
      </div>

      <!-- 分類標籤 -->
      <div class="category-tabs">
        <button
          v-for="cat in categories"
          :key="cat.key"
          type="button"
          :class="['category-tab', { active: selectedCategory === cat.key }]"
          @click="selectedCategory = cat.key"
          :title="cat.label"
        >
          <i :class="cat.icon"></i>
          <span class="category-label">{{ cat.label }}</span>
        </button>
      </div>

      <div class="icon-grid">
        <div
          v-for="icon in filteredIcons"
          :key="icon"
          class="icon-item"
          :class="{ 'selected': modelValue === icon }"
          @click="selectIcon(icon)"
          :title="icon"
        >
          <i :class="icon"></i>
        </div>
      </div>

      <div v-if="filteredIcons.length === 0" class="empty-state">
        沒有找到符合的圖示
      </div>
    </div>

    <!-- 點擊外部關閉 -->
    <div v-if="showPicker" class="picker-overlay" @click="closePicker"></div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { getIconCategoryFilters, getIconsByCategoryKey } from '@/constants/iconCatalog.js';

export default {
  name: 'IconPicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const showPicker = ref(false);
    const searchQuery = ref('');
    const selectedCategory = ref('all');

    const categories = computed(() => getIconCategoryFilters());

    const filteredIcons = computed(() => {
      let icons = getIconsByCategoryKey(selectedCategory.value);

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        icons = icons.filter(icon => icon.toLowerCase().includes(query));
      }

      return icons;
    });

    const togglePicker = () => {
      if (props.disabled) return;
      showPicker.value = !showPicker.value;
      if (showPicker.value) {
        searchQuery.value = '';
        selectedCategory.value = 'all';
      }
    };

    const closePicker = () => {
      showPicker.value = false;
    };

    const selectIcon = (icon) => {
      emit('update:modelValue', icon);
      closePicker();
    };

    const clearSelection = () => {
      emit('update:modelValue', '');
      closePicker();
    };

    const handleSearch = () => {
      // 搜尋功能已透過 computed 實現
    };

    return {
      showPicker,
      searchQuery,
      selectedCategory,
      categories,
      filteredIcons,
      togglePicker,
      closePicker,
      selectIcon,
      clearSelection,
      handleSearch
    };
  }
};
</script>

<style scoped>
.icon-picker {
  position: relative;
  width: 100%;
}

.selected-icon {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.selected-icon:hover:not(.disabled) {
  border-color: #007bff;
}

.selected-icon.disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.icon-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #f8f9fa;
}

.icon-preview i {
  font-size: 20px;
  color: #333;
}

.icon-preview .placeholder {
  font-size: 12px;
  color: #999;
}

.icon-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-family: 'Courier New', monospace;
}

.toggle-arrow {
  font-size: 16px;
  color: #666;
  transition: transform 0.2s;
}

.toggle-arrow.open {
  transform: rotate(180deg);
}

.icon-picker-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.picker-header {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  flex: 1;
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
}

.btn-clear {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-clear:hover {
  background: #f8f9fa;
  border-color: #999;
}

/* 分類標籤 */
.category-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  overflow-x: auto;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.category-tabs::-webkit-scrollbar {
  height: 4px;
}

.category-tabs::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: white;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.category-tab:hover {
  border-color: #007bff;
  background: #f0f7ff;
  color: #007bff;
}

.category-tab.active {
  border-color: #007bff;
  background: #007bff;
  color: white;
}

.category-tab i {
  font-size: 14px;
}

.category-label {
  font-weight: 500;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
  gap: 8px;
  padding: 12px;
  overflow-y: auto;
  max-height: 350px;
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.icon-item:hover {
  border-color: #007bff;
  background: #f0f7ff;
  transform: scale(1.1);
}

.icon-item.selected {
  border-color: #007bff;
  background: #007bff;
}

.icon-item i {
  font-size: 24px;
  color: #333;
}

.icon-item.selected i {
  color: white;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}
</style>
